import { getAssetDeals } from "actions/assetDeal";
import { showInfo, showSuccess } from "actions/notification";
import { getSpaces } from "actions/space";
import {
  askAssetQuestion as askQuestion,
  createAsset as create,
  deleteAsset as destroy,
  exportAssetsPdf,
  exportAssetsXls,
  exportPortfolioXls,
  getAsset as retrieveAsset,
  getAssetNames as retrieveAssetNames,
  getFilteredAssets as retrieveFilteredAssets,
  requestAssetFlyer as requestFlyer,
  subscribeAsset as subscribe,
  unsubscribeAsset as unsubscribe,
  update,
} from "api/asset";
import { push, replace } from "connected-react-router";
import { saveAs } from "file-saver";
import difference from "lodash/difference";
import flatMap from "lodash/flatMap";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import moment from "moment";

import { findById, isMobileDevice } from "utils";
import Translate from "utils/translate";
import { createUrlParams, getUrlParams } from "utils/urlParams";

const { t } = new Translate("notifications");

export const hasUrlParams = () => !isEmpty(omit(getUrlParams(), ["asset_id"]));

export const toggleSpaces = (assetID) => (dispatch, getState) => {
  const { spaceIds } = findById(getState().assets, assetID);
  const spaces = Object.values(getState().spaces);

  const expand = () =>
    dispatch({
      type: "ASSET_SPACES_TOGGLE",
      meta: { assetID },
    });

  if (spaces.some((space) => includes(spaceIds, space.id))) {
    return expand();
  }

  if (spaceIds.length === 0) {
    return expand();
  }

  return dispatch(getSpaces(spaceIds)).then(() => expand());
};

export const toggleDeals = (assetID) => (dispatch, getState) => {
  const asset = findById(getState().assets, assetID);
  const { assetDeals } = getState();
  const isPreviouslyFetched = assetDeals.some((assetDeal) => assetDeal.assetId === assetID);

  const toggle = () =>
    dispatch({
      type: "ASSET_DEALS_TOGGLE",
      meta: { assetID },
    });

  if (asset.isDealsExpanded || isPreviouslyFetched) {
    return toggle();
  }

  return dispatch(getAssetDeals(assetID, "active")).then(() => toggle());
};

export const toggleAllSpaces = () => (dispatch, getState) => {
  let spaceIDs = flatMap(getState().assets, (a) => a.spaceIds);
  spaceIDs = difference(
    spaceIDs,
    Object.values(getState().spaces).map((s) => s.id)
  );
  const expand = () =>
    dispatch({
      type: "ASSET_SPACES_TOGGLE_ALL",
    });
  if (!spaceIDs.length) return expand();

  return dispatch(getSpaces(spaceIDs)).then(() => expand());
};

export const getAssets =
  (values, page = 1) =>
  (dispatch, getState) => {
    const { isFiltering } = values;
    const filters = omit(values, ["isFiltering"]);
    const { assetSort } = getState();
    filters.sort = assetSort;

    dispatch(replace(`/assets${createUrlParams(filters)}`));

    return dispatch({
      type: "ASSETS_GET",
      payload: retrieveFilteredAssets(filters, page),
      meta: { filters, page, isFiltering },
    }).then(() => isFiltering && dispatch(toggleAllSpaces()));
  };

export const getAssetNames = () => ({
  type: "ASSET_NAMES_GET",
  payload: retrieveAssetNames(),
});

export const exportAssetsAsXls = () => (dispatch, getState) => {
  let exportAction = null;
  const portfolioIDs = getState().selectedPortfolioIDs;
  const parameters = getUrlParams();

  if (hasUrlParams()) {
    exportAction = {
      type: "ASSETS_EXPORT_XLS",
      payload: exportAssetsXls(portfolioIDs, parameters),
      meta: { portfolioIDs, filter: parameters },
    };
  } else {
    exportAction = {
      type: "ASSETS_EXPORT_XLS",
      payload: exportPortfolioXls(portfolioIDs),
      meta: { portfolioIDs },
    };
  }

  return dispatch(exportAction).then((res) =>
    res.value.blob().then((xls) => {
      const blob = new Blob([xls], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `rialto_assets_export_${moment().format("L")}.xlsx`);
    })
  );
};

export const exportAssetsAsPdf = () => (dispatch, getState) =>
  dispatch({
    type: "ASSETS_EXPORT_PDF",
    payload: exportAssetsPdf(
      encodeURIComponent(window.location.href),
      getState().currentUser.fullName
    ),
  }).then(() =>
    dispatch(showInfo(t("asset.exportPdf.success.title"), t("asset.exportPdf.success.message")))
  );

export const getAsset = (assetID) => ({
  type: "ASSET_GET",
  payload: retrieveAsset(assetID),
});

export const requestAssetFlyer = (assetID) => (dispatch) =>
  dispatch({
    type: "ASSET_REQUEST_FLYER",
    payload: requestFlyer(assetID),
    meta: { assetID },
  }).then(() =>
    dispatch(
      showInfo(t("asset.requestFlyer.success.title"), t("asset.requestFlyer.success.message"))
    )
  );

export const subscribeAsset = (assetID) => ({
  type: "ASSET_SUBSCRIBE",
  payload: subscribe(assetID),
  meta: { assetID },
});

export const unsubscribeAsset = (assetID) => ({
  type: "ASSET_UNSUBSCRIBE",
  payload: unsubscribe(assetID),
  meta: { assetID },
});

export const askAssetQuestion = (assetID, topic, question) => ({
  type: "ASSET_QUESTION",
  payload: askQuestion(assetID, topic, question),
  meta: { assetID },
});

export const createAsset = (asset) => (dispatch, getState) =>
  dispatch({
    type: "ASSET_CREATE",
    payload: create({
      ...asset,
      countryId: asset.country.id,
      pointPersonId: asset.pointPerson.id,
      portfolioId: asset.portfolio.id,
      regionId: asset.region && asset.region.id,
      termsOfServiceID:
        getState().currentUser.company.termsOfService &&
        getState().currentUser.company.termsOfService.id,
    }),
  });

export const updateAsset = (asset) => ({
  type: "ASSET_UPDATE",
  payload: update(asset),
});

export const changeAssetPortfolio = (asset) => ({
  type: "ASSET_PORTFOLIO_CHANGE",
  payload: update(asset),
});

export const deleteAsset = (assetID, status) => (dispatch, getState) => {
  const portfolioID = getState().asset.portfolio.id;

  return dispatch({
    type: "ASSET_DELETE",
    payload: destroy(assetID, status),
  }).then(() => {
    dispatch(showSuccess(t("asset.delete.success.title")));

    return dispatch(push(`/assets?portfolioIDs[]=${portfolioID}`));
  });
};

export const flyerDownload = (asset) => (dispatch) => {
  window.open(asset.flyer.url, "_blank");

  return dispatch({
    type: "ASSET_FLYER_DOWNLOAD",
    meta: { assetID: asset.id },
  });
};

export const flyerOpen = (asset) => (dispatch) => {
  window.open(
    asset.customFlyerUrl ? asset.customFlyerUrl : `/flyers/${asset.id}`,
    isMobileDevice() ? "_self" : "_blank"
  );

  return dispatch({
    type: "ASSET_FLYER_OPEN",
    meta: { assetID: asset.id },
  });
};

export const phoneReveal = (assetID) => ({
  type: "ASSET_PHONE_REVEAL",
  meta: { assetID },
});

export const assetSort = (sort) => ({
  type: "ASSET_SORT",
  meta: { sort },
});
